// Auth helpers — JWT in localStorage, shared across the SPA.

import { APP_ID, type AuthResponse, type User } from '@funderpro/shared';

const TOKEN_KEY = 'fp_access_token';
const REFRESH_KEY = 'fp_refresh_token';
const USER_KEY = 'fp_user';

export function getToken(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
}
export function getRefreshToken(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem(REFRESH_KEY) : null;
}
export function getUser(): User | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as User; } catch { return null; }
}
export function setAuth(res: AuthResponse) {
  localStorage.setItem(TOKEN_KEY, res.access_token);
  localStorage.setItem(REFRESH_KEY, res.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(res.user));
}
export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export const AUTH_API = {
  register: (email: string, password: string, full_name?: string) =>
    fetchAuth(AUTH_PATHS.register, { method: 'POST', body: { email, password, full_name } }),
  login: (email: string, password: string) =>
    fetchAuth(AUTH_PATHS.login, { method: 'POST', body: { email, password } }),
  verifyOtp: (email: string, otp_code: string) =>
    fetchAuth(AUTH_PATHS.verifyOtp, { method: 'POST', body: { email, otp_code } }),
  resendOtp: (email: string) =>
    fetchAuth(AUTH_PATHS.resendOtp, { method: 'POST', body: { email } }),
  forgot: (email: string) =>
    fetchAuth(AUTH_PATHS.forgot, { method: 'POST', body: { email } }),
  reset: (reset_token: string, new_password: string) =>
    fetchAuth(AUTH_PATHS.reset, { method: 'POST', body: { reset_token, new_password } }),
  me: () => fetchAuth(AUTH_PATHS.me, { method: 'GET' }, true),
};

async function fetchAuth(path: string, init: { method: string; body?: unknown }, useToken = false): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (useToken) {
    const t = getToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  const res = await fetch(path, {
    method: init.method,
    headers,
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  let data: any = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const message = data?.message || data?.detail || `Request failed: ${res.status}`;
    throw Object.assign(new Error(message), { status: res.status, body: data });
  }
  return data;
}

export function logout() {
  clearAuth();
  window.location.href = '/';
}

export interface AuthSnapshot {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
}

export function getAuth(): AuthSnapshot {
  return { token: getToken(), refreshToken: getRefreshToken(), user: getUser() };
}

// Resolve the API base: env override → production worker → local worker
export const API_BASE = (() => {
  if (typeof window === 'undefined') return 'https://funderpro-investment.rustybm.workers.dev';
  // In dev, the Vite proxy handles /api. In prod, we hit the worker directly.
  if (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.local')) {
    return '';  // same origin (Vite proxy)
  }
  return 'https://funderpro-investment.rustybm.workers.dev';
})();

export const AUTH_PATHS = {
  register: `${API_BASE}/api/apps/${APP_ID}/auth/register`,
  login:    `${API_BASE}/api/apps/${APP_ID}/auth/login`,
  verifyOtp:`${API_BASE}/api/apps/${APP_ID}/auth/verify-otp`,
  resendOtp:`${API_BASE}/api/apps/${APP_ID}/auth/resend-otp`,
  forgot:   `${API_BASE}/api/apps/${APP_ID}/auth/reset-password-request`,
  reset:    `${API_BASE}/api/apps/${APP_ID}/auth/reset-password`,
  me:       `${API_BASE}/api/apps/${APP_ID}/auth/me`,
};
